/**
 * Card Styles Configuration
 *
 * Centralized configuration for gift card visual styles.
 * Each style has:
 * - id: Numeric ID stored in claim URL (keeps URLs compact)
 * - name: Display name for UI
 * - background: CSS gradient for card background (fallback)
 * - image: Optional background image URL (JPEG format)
 * - imageWebP: Optional WebP version of background image (~70% smaller)
 * - thumbnail: Optional thumbnail image URL for selector buttons (120x120px JPEG)
 * - thumbnailWebP: Optional WebP thumbnail (~60% smaller than JPEG thumbnail)
 *
 * Image Fallback Chain:
 * 1. Full card: WebP (imageWebP) → JPEG (image) → gradient (background)
 * 2. Thumbnails: WebP (thumbnailWebP) → JPEG (thumbnail) → gradient (background)
 *
 * Image can be:
 * - Local path: '/cards/1.jpg' (served from public/)
 * - External URL: 'https://cdn.example.com/cards/1.jpg'
 * - null: Use gradient only
 *
 * Note: Images are automatically optimized during build via npm run process-images
 */

export const CARD_STYLES = [
  {
    id: 0,
    name: 'Cucumber',
    background: 'linear-gradient(222.14deg, #255A28 7.63%, #08380B 23.47%, #255A28 76.25%)',
    image: "/cards/1.jpg",
    imageWebP: "/cards/1.webp",
    thumbnail: "/cards/thumbs/1.jpg",
    thumbnailWebP: "/cards/thumbs/1.webp"
  },
  {
    id: 1,
    name: 'Duck',
    background: 'linear-gradient(222.14deg, #518BF6 7.63%, #62B8F9 76.25%)',
    image: "/cards/2.jpg",
    imageWebP: "/cards/2.webp",
    thumbnail: "/cards/thumbs/2.jpg",
    thumbnailWebP: "/cards/thumbs/2.webp"
  },
  {
    id: 2,
    name: 'Birthday',
    background: 'linear-gradient(222.14deg, #48E394 7.63%, #487678 76.25%)',
    image: "/cards/3.jpg",
    imageWebP: "/cards/3.webp",
    thumbnail: "/cards/thumbs/3.jpg",
    thumbnailWebP: "/cards/thumbs/3.webp"
  },
  {
    id: 3,
    name: 'Magic',
    background: 'linear-gradient(222.14deg, #393E40 7.63%, #487678 76.25%)',
    image: "/cards/4.jpg",
    imageWebP: "/cards/4.webp",
    thumbnail: "/cards/thumbs/4.jpg",
    thumbnailWebP: "/cards/thumbs/4.webp"
  },
  {
    id: 4,
    name: 'Xmas',
    background: 'linear-gradient(222.14deg, #ED673D 7.63%, #F28648 76.25%)',
    image: "/cards/5.jpg",
    imageWebP: "/cards/5.webp",
    thumbnail: "/cards/thumbs/5.jpg",
    thumbnailWebP: "/cards/thumbs/5.webp"
  }
]

/**
 * Default card style ID used when none specified
 */
export const DEFAULT_STYLE_ID = 0

/**
 * Get card style by ID
 * Returns default style if ID not found
 *
 * @param {number} id - Style ID (0-4)
 * @returns {object} Card style object
 */
export function getCardStyle(id) {
  if (typeof id !== 'number') {
    return CARD_STYLES[DEFAULT_STYLE_ID]
  }

  const style = CARD_STYLES.find(s => s.id === id)
  return style || CARD_STYLES[DEFAULT_STYLE_ID]
}

/**
 * Get all available card styles
 *
 * @returns {Array} Array of all card styles
 */
export function getAllCardStyles() {
  return [...CARD_STYLES]
}

/**
 * Get mirrored gradient for card back side
 * Inverts the gradient direction and swaps colors for a mirrored effect
 *
 * @param {number} id - Style ID (0-4)
 * @returns {string} Mirrored CSS gradient string
 */
export function getMirroredGradient(id) {
  const style = getCardStyle(id)
  const gradient = style.background

  // Parse gradient: linear-gradient(222.14deg, #COLOR1 7.63%, #COLOR2 76.25%)
  const match = gradient.match(/linear-gradient\(([^,]+),\s*([^,]+),\s*(.+)\)/)

  if (!match) {
    // Fallback: return original if parsing fails
    return gradient
  }

  const [, angle, color1, color2] = match

  // Invert angle: 222.14deg + 180 = 402.14deg = 42.14deg
  const degrees = parseFloat(angle)
  const mirroredAngle = ((degrees + 180) % 360).toFixed(2)

  // Swap colors to create mirrored effect
  return `linear-gradient(${mirroredAngle}deg, ${color2}, ${color1})`
}
